(() => {
  const viewKey = 'chicaup-catalog-view';
  let messageTimer;

  const getCatalogRoot = (element) => element.closest('[data-marketplace-catalog]') || document;

  const setCatalogView = (root, view) => {
    const grid = root.querySelector('.product-grid');
    if (!grid) return;

    grid.classList.toggle('catalog-grid--compact', view === 'compact');
    root.querySelectorAll('[data-catalog-view]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.catalogView === view));
    });
  };

  const navigateWithUrl = (updateUrl) => {
    const url = new URL(window.location.href);
    updateUrl(url);
    url.searchParams.delete('page');
    window.location.assign(url.toString());
  };

  const navigateWithParam = (name, value) => {
    navigateWithUrl((url) => url.searchParams.set(name, value));
  };

  const closeToolsPanel = (panel) => {
    if (!panel) return;
    panel.hidden = true;
    const root = getCatalogRoot(panel);
    const toggle = root.querySelector(`[aria-controls="${panel.id}"]`);
    const backdrop = root.querySelector('[data-tools-backdrop]');
    toggle?.setAttribute('aria-expanded', 'false');
    if (backdrop) backdrop.hidden = true;
    document.body.classList.remove('marketplace-tools-open');
  };

  const closeAllToolsPanels = () => {
    document.querySelectorAll('[data-tools-panel]:not([hidden])').forEach(closeToolsPanel);
  };

  const setToolsPanelOpen = (toggle, open) => {
    const panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!panel) return;
    closeAllToolsPanels();
    panel.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    const backdrop = getCatalogRoot(toggle).querySelector('[data-tools-backdrop]');
    if (backdrop) backdrop.hidden = !open;
    document.body.classList.toggle('marketplace-tools-open', open);
    if (open) panel.querySelector('[data-tools-close]')?.focus();
  };

  const showToolMessage = (source, message) => {
    const status = getCatalogRoot(source).querySelector('[data-tool-message]');
    if (!status) return;
    window.clearTimeout(messageTimer);
    status.textContent = message;
    status.hidden = false;
    messageTimer = window.setTimeout(() => {
      status.hidden = true;
    }, 2400);
  };

  const getToolMessage = (source, key) => {
    const toolbar = source.closest('.marketplace-toolbar');
    return toolbar?.dataset[key] || '';
  };

  const copyText = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    textarea.remove();
  };

  const initProductCarousel = (carousel) => {
    if (carousel.dataset.carouselReady === 'true') return;

    const viewport = carousel.querySelector('[data-carousel-viewport]');
    const track = carousel.querySelector('.marketplace-product-carousel__track');
    const previousButton = carousel.querySelector('[data-carousel-previous]');
    const nextButton = carousel.querySelector('[data-carousel-next]');
    const toggleButton = carousel.querySelector('[data-carousel-toggle]');
    if (!viewport || !track || !previousButton || !nextButton || !toggleButton) return;

    carousel.dataset.carouselReady = 'true';
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const delay = Number(carousel.dataset.autoplayDelay) || 4000;
    let timer;
    let userPaused = reduceMotion;
    let interactionPaused = false;

    const cardStep = () => {
      const item = track.querySelector('[data-carousel-item]');
      if (!item) return viewport.clientWidth;
      const gap = Number.parseFloat(window.getComputedStyle(track).columnGap) || 0;
      return item.getBoundingClientRect().width + gap;
    };

    const move = (direction) => {
      const step = cardStep();
      const maximum = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      let nextPosition = viewport.scrollLeft + step * direction;
      if (direction > 0 && nextPosition > maximum - 2) nextPosition = 0;
      if (direction < 0 && nextPosition < 2) nextPosition = maximum;
      viewport.scrollTo({ left: nextPosition, behavior: reduceMotion ? 'auto' : 'smooth' });
    };

    const schedule = () => {
      window.clearInterval(timer);
      if (!userPaused && !interactionPaused && !document.hidden) {
        timer = window.setInterval(() => move(1), delay);
      }
    };

    const setUserPaused = (paused) => {
      userPaused = paused;
      toggleButton.setAttribute('aria-pressed', String(paused));
      toggleButton.setAttribute('aria-label', paused ? toggleButton.dataset.labelPlay : toggleButton.dataset.labelPause);
      schedule();
    };

    previousButton.addEventListener('click', () => {
      move(-1);
      schedule();
    });
    nextButton.addEventListener('click', () => {
      move(1);
      schedule();
    });
    toggleButton.addEventListener('click', () => setUserPaused(!userPaused));
    viewport.addEventListener('pointerenter', () => {
      interactionPaused = true;
      schedule();
    });
    viewport.addEventListener('pointerleave', () => {
      interactionPaused = false;
      schedule();
    });
    viewport.addEventListener('focusin', () => {
      interactionPaused = true;
      schedule();
    });
    viewport.addEventListener('focusout', (event) => {
      if (!viewport.contains(event.relatedTarget)) {
        interactionPaused = false;
        schedule();
      }
    });
    document.addEventListener('visibilitychange', schedule);
    setUserPaused(userPaused);
  };

  const initFloatingGuide = (guide) => {
    if (guide.dataset.guideReady === 'true') return;

    const dragHandle = guide.querySelector('[data-guide-drag]');
    const toggleButton = guide.querySelector('[data-guide-toggle]');
    const content = guide.querySelector('[data-guide-content]');
    const topButton = guide.querySelector('[data-guide-top]');
    if (!dragHandle || !toggleButton || !content || !topButton) return;

    guide.dataset.guideReady = 'true';
    const positionKey = 'chicaup-floating-guide-position';
    const expandedKey = 'chicaup-floating-guide-expanded';
    let holdTimer;
    let pointerId;
    let dragging = false;
    let startX = 0;
    let startY = 0;
    let offsetX = 0;
    let offsetY = 0;
    let initialLeft = 0;
    let initialTop = 0;

    const setExpanded = (expanded, persist = true) => {
      toggleButton.setAttribute('aria-expanded', String(expanded));
      toggleButton.setAttribute('aria-label', expanded ? toggleButton.dataset.labelMinimize : toggleButton.dataset.labelExpand);
      content.hidden = !expanded;
      if (persist) {
        try {
          window.localStorage.setItem(expandedKey, String(expanded));
        } catch (error) {
          // The guide remains usable when browser storage is unavailable.
        }
      }
    };

    const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), Math.max(minimum, maximum));

    const placeGuide = (left, top) => {
      const margin = 8;
      const width = guide.offsetWidth;
      const height = guide.offsetHeight;
      guide.style.right = 'auto';
      guide.style.bottom = 'auto';
      guide.style.transform = 'none';
      guide.style.left = `${clamp(left, margin, window.innerWidth - width - margin)}px`;
      guide.style.top = `${clamp(top, margin, window.innerHeight - height - margin)}px`;
    };

    try {
      const storedExpanded = window.localStorage.getItem(expandedKey);
      setExpanded(storedExpanded === null ? false : storedExpanded === 'true', false);
      const storedPosition = JSON.parse(window.localStorage.getItem(positionKey) || 'null');
      if (storedPosition && Number.isFinite(storedPosition.left) && Number.isFinite(storedPosition.top)) {
        placeGuide(storedPosition.left, storedPosition.top);
      }
    } catch (error) {
      setExpanded(false, false);
    }

    toggleButton.addEventListener('click', () => {
      setExpanded(toggleButton.getAttribute('aria-expanded') !== 'true');
      if (guide.style.left) placeGuide(Number.parseFloat(guide.style.left), Number.parseFloat(guide.style.top));
    });

    topButton.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    });

    const beginDrag = () => {
      if (dragging) return;
      dragging = true;
      guide.classList.add('is-dragging');
      placeGuide(initialLeft, initialTop);
    };

    dragHandle.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      const rect = guide.getBoundingClientRect();
      initialLeft = rect.left;
      initialTop = rect.top;
      offsetX = event.clientX - rect.left;
      offsetY = event.clientY - rect.top;
      dragHandle.setPointerCapture(pointerId);
      window.clearTimeout(holdTimer);
      holdTimer = window.setTimeout(beginDrag, 350);
    });

    dragHandle.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      if (!dragging) {
        if (Math.hypot(event.clientX - startX, event.clientY - startY) <= 8) return;
        window.clearTimeout(holdTimer);
        beginDrag();
      }
      event.preventDefault();
      placeGuide(event.clientX - offsetX, event.clientY - offsetY);
    });

    const finishDrag = (event) => {
      if (event.pointerId !== pointerId) return;
      window.clearTimeout(holdTimer);
      if (dragging) {
        dragging = false;
        guide.classList.remove('is-dragging');
        try {
          window.localStorage.setItem(
            positionKey,
            JSON.stringify({ left: Number.parseFloat(guide.style.left), top: Number.parseFloat(guide.style.top) })
          );
        } catch (error) {
          // Position persistence is optional.
        }
      }
      if (dragHandle.hasPointerCapture(pointerId)) dragHandle.releasePointerCapture(pointerId);
      pointerId = undefined;
    };

    dragHandle.addEventListener('pointerup', finishDrag);
    dragHandle.addEventListener('pointercancel', finishDrag);
    window.addEventListener('resize', () => {
      if (guide.style.left) placeGuide(Number.parseFloat(guide.style.left), Number.parseFloat(guide.style.top));
    });
  };

  document.addEventListener('click', async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const toolsToggle = target.closest('[data-tools-toggle]');
    if (toolsToggle) {
      const shouldOpen = toolsToggle.getAttribute('aria-expanded') !== 'true';
      setToolsPanelOpen(toolsToggle, shouldOpen);
      return;
    }

    const toolsClose = target.closest('[data-tools-close], [data-tools-backdrop]');
    if (toolsClose) {
      closeAllToolsPanels();
      return;
    }

    const openPanel = document.querySelector('[data-tools-panel]:not([hidden])');
    if (openPanel && !target.closest('[data-tools-panel]')) closeToolsPanel(openPanel);

    const viewButton = target.closest('[data-catalog-view]');
    if (viewButton) {
      const root = getCatalogRoot(viewButton);
      const view = viewButton.dataset.catalogView;
      setCatalogView(root, view);
      try {
        window.localStorage.setItem(viewKey, view);
      } catch (error) {
        // The visual control still works when browser storage is unavailable.
      }
      return;
    }

    const sortButton = target.closest('[data-catalog-sort]');
    if (sortButton) {
      navigateWithParam('sort_by', sortButton.dataset.catalogSort);
      return;
    }

    const stockButton = target.closest('[data-catalog-stock]');
    if (stockButton) {
      navigateWithUrl((url) => {
        const isActive = url.searchParams.get('filter.v.availability') === '1';
        if (isActive) {
          url.searchParams.delete('filter.v.availability');
        } else {
          url.searchParams.set('filter.v.availability', '1');
        }
      });
      return;
    }

    const availabilityButton = target.closest('[data-catalog-availability]');
    if (availabilityButton) {
      navigateWithParam('filter.v.availability', availabilityButton.dataset.catalogAvailability);
      return;
    }

    const priceButton = target.closest('[data-price-min], [data-price-max]');
    if (priceButton) {
      navigateWithUrl((url) => {
        url.searchParams.delete('filter.v.price.gte');
        url.searchParams.delete('filter.v.price.lte');
        if (priceButton.dataset.priceMin) {
          url.searchParams.set('filter.v.price.gte', priceButton.dataset.priceMin);
        }
        if (priceButton.dataset.priceMax) {
          url.searchParams.set('filter.v.price.lte', priceButton.dataset.priceMax);
        }
      });
      return;
    }

    const clearButton = target.closest('[data-clear-catalog-filters]');
    if (clearButton) {
      navigateWithUrl((url) => {
        Array.from(url.searchParams.keys()).forEach((key) => {
          if (key.startsWith('filter.')) url.searchParams.delete(key);
        });
        url.searchParams.delete('sort_by');
      });
      return;
    }

    const copyButton = target.closest('[data-copy-catalog-link]');
    if (copyButton) {
      try {
        await copyText(window.location.href);
        showToolMessage(copyButton, getToolMessage(copyButton, 'messageLinkCopied'));
      } catch (error) {
        showToolMessage(copyButton, getToolMessage(copyButton, 'messageCopyFailed'));
      }
      return;
    }

    const shareButton = target.closest('[data-share-catalog]');
    if (shareButton) {
      try {
        if (navigator.share) {
          await navigator.share({ title: document.title, url: window.location.href });
          showToolMessage(shareButton, getToolMessage(shareButton, 'messageShared'));
        } else {
          await copyText(window.location.href);
          showToolMessage(shareButton, getToolMessage(shareButton, 'messageLinkCopied'));
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          showToolMessage(shareButton, getToolMessage(shareButton, 'messageShareFailed'));
        }
      }
      return;
    }

    const printButton = target.closest('[data-print-catalog]');
    if (printButton) {
      closeAllToolsPanels();
      window.print();
      return;
    }

    const sidebarButton = target.closest('[data-sidebar-toggle]');
    if (sidebarButton) {
      const sidebar = document.getElementById(sidebarButton.getAttribute('aria-controls'));
      if (!sidebar) return;
      const isOpen = sidebar.classList.toggle('is-open');
      sidebarButton.setAttribute('aria-expanded', String(isOpen));
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAllToolsPanels();
  });

  document.addEventListener('DOMContentLoaded', () => {
    let savedView = 'comfortable';
    try {
      savedView = window.localStorage.getItem(viewKey) || savedView;
    } catch (error) {
      savedView = 'comfortable';
    }

    document.querySelectorAll('[data-marketplace-catalog]').forEach((root) => setCatalogView(root, savedView));

    const url = new URL(window.location.href);
    const availability = url.searchParams.get('filter.v.availability');
    const priceMin = url.searchParams.get('filter.v.price.gte') || '';
    const priceMax = url.searchParams.get('filter.v.price.lte') || '';

    document.querySelectorAll('[data-catalog-stock]').forEach((button) => {
      button.setAttribute('aria-pressed', String(availability === '1'));
    });
    document.querySelectorAll('[data-catalog-availability]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.catalogAvailability === availability));
    });
    document.querySelectorAll('[data-price-min], [data-price-max]').forEach((button) => {
      const isActive = (button.dataset.priceMin || '') === priceMin && (button.dataset.priceMax || '') === priceMax;
      button.setAttribute('aria-pressed', String(isActive));
    });

    document.querySelectorAll('[data-product-carousel]').forEach(initProductCarousel);
    document.querySelectorAll('[data-floating-guide]').forEach(initFloatingGuide);
  });

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-product-carousel]').forEach(initProductCarousel);
    event.target.querySelectorAll('[data-floating-guide]').forEach(initFloatingGuide);
  });
})();
